import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import type { IReactionDisposer } from "mobx";
import type { To } from "react-router";

import { PusherEventKind } from "@/domains/pusher/constants";
import type {
  AgenticSearchPatchEventData,
  AgenticSearchResultUpdate,
  AgenticSearchResultTarget,
} from "@/domains/pusher/constants";
import type { Uuid } from "@/domains/global/identifiers";
import type { PusherClient } from "@/modules/pusher-client/types";
import { urlParamsModule } from "@/modules/url-params";
import type {
  SearchEngineRequest,
  SearchEngineResult,
} from "@/modules/url-params/search-engine-params/types";
import type { ApiSchema } from "@/modules/api";
import { EventContext } from "@/domains/metrics/context";
import { convertDateParamsToSearchEngineFilters } from "@/modules/filters/common/convertDateParamsToSearchEngineFilters";
import { SearchPageFilter } from "@/modules/filters/search/SearchPageFilter";
import { SortByKind } from "@/modules/lenses/types";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { AppSubStore } from "@/store/types";
import type { AppSubStoreArgs } from "@/store/types";
import type { INoteObservable } from "@/store/note";
import type { IImageLightboxImage } from "@/components/note/media-viewer/image/image-lightbox.types";
import { buildProxiedAssetUrl, resolveMatchingAttachmentByUrl } from "@/modules/asset-proxy";
import { noteModule } from "@mem-labs/common-editor";
import {
  SearchResultImageThumbnailItem,
  SearchResultPdfChipItem,
} from "@/pages/search/components/search-result-pdf-chips";
import {
  classifyProxyAttachmentUrl,
  resolveSearchResultAttachmentMediaOrder,
} from "@/store/pages/SearchPageStore/SearchPageStore";
import {
  resolveSearchResultFeaturedSnippet,
  type SearchResultFeaturedSnippetRows,
} from "@/store/pages/SearchPageStore/resolveSearchResultFeaturedSnippet";

export type AgentSearchResultGroup = {
  id: string;
  label: string;
  resultIds: string[];
};

export type AgentSearchFastSectionRow =
  | { kind: "heading"; result: SearchEngineResult }
  | { kind: "carousel"; result: SearchEngineResult }
  | { kind: "result"; resultId: string };

export type AgentSearchResultRow =
  | { kind: "group"; group: AgentSearchResultGroup }
  | { kind: "otherResultsHeader" }
  | { kind: "heading"; result: SearchEngineResult }
  | { kind: "carousel"; result: SearchEngineResult }
  | { kind: "result"; result: SearchEngineResult };

export type AgentSearchAttachmentPreview = {
  pdfChips: SearchResultPdfChipItem[];
  imageThumbnails: SearchResultImageThumbnailItem[];
  mediaOrder: ReturnType<typeof resolveSearchResultAttachmentMediaOrder>;
};

interface AgentSearchPageStoreArgs extends AppSubStoreArgs {
  path: To;
  tabId: string;
}

const AGENT_SEARCH_STATUS_MESSAGE_DISMISS_DELAY_MS = 1_500;
const AGENT_SEARCH_RUN_TIMEOUT_MS = 90_000;

export class AgentSearchPageStore extends AppSubStore {
  path: To;
  tabId: string;
  filters: SearchPageFilter;
  listState: ListStateObservable;
  eventContext = EventContext.SearchMultiselectActions;
  supportsAddToCollectionListAction = true;
  supportsMoveNoteToTrashListAction = true;
  supportsChatListAction = true;
  query = "";
  agenticSearchSessionId: string | null = null;
  pusherChannelName: string | null = null;
  runId: string | null = null;
  runMode: "local" | "temporal" | null = null;
  previousPathSearch: string | null = null;
  latestSequence = 0;
  statusMessage = "";
  isLoading = false;
  isLoadingNextPage = false;
  isAgentRunning = false;
  isDone = false;
  errorMessage: string | null = null;
  resultById = new Map<string, SearchEngineResult>();
  visibleResultIds: string[] = [];
  appendedResultIds = new Set<string>();
  highlightedResultIds = new Set<string>();
  resultHints = new Map<string, string>();
  groups: AgentSearchResultGroup[] = [];
  fastSearchSectionRows: AgentSearchFastSectionRow[] = [];
  hasAgentReordered = false;
  nextFastSearchCursor: string | null = null;
  pdfLightboxState: AgentSearchPdfLightboxState | null = null;
  imageLightboxState: AgentSearchImageLightboxState | null = null;
  pusherChannel: ReturnType<PusherClient["subscribe"]> | null = null;
  private searchRequestId = 0;
  private dateFilterDisposer: IReactionDisposer;
  private prefetchedAttachmentContentNoteIds = new Set<string>();
  private statusMessageDismissTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private agentRunTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor({ path, tabId, ...args }: AgentSearchPageStoreArgs) {
    super(args);
    this.path = path;
    this.tabId = tabId;
    this.filters = new SearchPageFilter(this.store, path, {
      navigateToSearch: searchParams => {
        this.store.navigation.goToAgentSearch({
          searchParams,
          openInSameTab: true,
        });
      },
    });
    this.listState = new ListStateObservable({
      ...args,
      listStateProvider: this,
      canSelect: false,
    });
    this.dateFilterDisposer = reaction(
      () => this.filters.dateParams,
      () => {
        if (this.query.trim()) void this.search();
      }
    );
    makeObservable<
      this,
      | "resetSearchState"
      | "buildSearchRequest"
      | "subscribe"
      | "unsubscribe"
      | "addResults"
      | "addFastSearchResults"
      | "hasRenderableFastSearchSectionRows"
      | "registerItemResult"
      | "registerUpdateTargetResult"
      | "applyResultUpdates"
      | "applyGroups"
      | "applyReorder"
      | "resolveTarget"
      | "resolveResultId"
      | "resolveExistingEntityResultId"
      | "normalizeResult"
      | "searchFromPath"
      | "getResultItem"
      | "getResultValue"
      | "prefetchAttachmentPreviewContent"
      | "resolveProxiedAttachmentUrl"
      | "openPdfLightbox"
      | "openImageLightbox"
      | "resolveAttachmentMatches"
      | "resolvePdfAttachmentMatches"
      | "resolveImageAttachmentMatches"
      | "resolvePdfChipItems"
      | "resolveImageThumbnailItems"
      | "openPdfAttachmentLightbox"
      | "resolveAttachmentsFromLoadedNoteContent"
      | "resolveFileAttachmentsFromLoadedNoteContent"
      | "resolveImageAttachmentsFromLoadedNoteContent"
      | "resolveMatchingFileAttachment"
      | "resolveMatchingImageAttachment"
      | "resolveAttachmentUrlFromNoteContent"
      | "resolvePathSearch"
      | "searchRequestId"
      | "dateFilterDisposer"
      | "prefetchedAttachmentContentNoteIds"
      | "statusMessageDismissTimeoutId"
      | "agentRunTimeoutId"
      | "clearStatusMessageDismissTimeout"
      | "scheduleStatusMessageDismiss"
      | "clearAgentRunTimeout"
      | "scheduleAgentRunTimeout"
      | "forceAgentRunDone"
    >(this, {
      path: observable,
      tabId: observable,
      filters: false,
      listState: false,
      eventContext: false,
      supportsAddToCollectionListAction: false,
      supportsMoveNoteToTrashListAction: false,
      supportsChatListAction: false,
      query: observable,
      agenticSearchSessionId: observable,
      pusherChannelName: observable,
      runId: observable,
      runMode: observable,
      previousPathSearch: observable,
      latestSequence: observable,
      statusMessage: observable,
      isLoading: observable,
      isLoadingNextPage: observable,
      isAgentRunning: observable,
      isDone: observable,
      errorMessage: observable,
      resultById: observable,
      visibleResultIds: observable,
      appendedResultIds: observable,
      highlightedResultIds: observable,
      resultHints: observable,
      groups: observable,
      fastSearchSectionRows: observable,
      hasAgentReordered: observable,
      nextFastSearchCursor: observable,
      pdfLightboxState: observable,
      imageLightboxState: observable,
      pusherChannel: false,
      searchRequestId: false,
      dateFilterDisposer: false,
      prefetchedAttachmentContentNoteIds: false,
      statusMessageDismissTimeoutId: false,
      agentRunTimeoutId: false,
      clearStatusMessageDismissTimeout: false,
      scheduleStatusMessageDismiss: false,
      clearAgentRunTimeout: false,
      scheduleAgentRunTimeout: false,
      forceAgentRunDone: action.bound,
      rows: computed,
      orderedItemIds: computed,
      initialize: action.bound,
      setPath: action.bound,
      setQuery: action.bound,
      submitSearch: action.bound,
      search: action.bound,
      loadNextFastPage: action.bound,
      handlePatch: action.bound,
      getResultId: action.bound,
      getResultTitle: action.bound,
      getResultPreview: action.bound,
      getHeadingLabel: false,
      getResultSnippetRows: false,
      getResultNoteId: false,
      getResultCollectionId: false,
      getResultHint: false,
      getAttachmentPreview: false,
      closePdfLightbox: action.bound,
      closeImageLightbox: action.bound,
      dispose: action.bound,
      resetSearchState: action.bound,
      buildSearchRequest: false,
      subscribe: action.bound,
      unsubscribe: action.bound,
      addResults: action.bound,
      addFastSearchResults: action.bound,
      hasRenderableFastSearchSectionRows: false,
      registerItemResult: action.bound,
      registerUpdateTargetResult: action.bound,
      applyResultUpdates: action.bound,
      applyGroups: action.bound,
      applyReorder: action.bound,
      resolveTarget: false,
      resolveResultId: false,
      resolveExistingEntityResultId: false,
      normalizeResult: false,
      searchFromPath: action.bound,
      getResultItem: false,
      getResultValue: false,
      prefetchAttachmentPreviewContent: false,
      resolveProxiedAttachmentUrl: false,
      openPdfLightbox: false,
      openImageLightbox: false,
      resolveAttachmentMatches: false,
      resolvePdfAttachmentMatches: false,
      resolveImageAttachmentMatches: false,
      resolvePdfChipItems: false,
      resolveImageThumbnailItems: false,
      openPdfAttachmentLightbox: false,
      resolveAttachmentsFromLoadedNoteContent: false,
      resolveFileAttachmentsFromLoadedNoteContent: false,
      resolveImageAttachmentsFromLoadedNoteContent: false,
      resolveMatchingFileAttachment: false,
      resolveMatchingImageAttachment: false,
      resolveAttachmentUrlFromNoteContent: false,
      resolvePathSearch: false,
    });
  }

  initialize() {
    this.searchFromPath();
  }

  setPath(path: To) {
    this.path = path;
    this.filters.setTabPath(path);
    this.searchFromPath();
  }

  private searchFromPath() {
    const searchQueryStr = this.resolvePathSearch();
    if (searchQueryStr === this.previousPathSearch) return;

    const searchParams = urlParamsModule.search.parse({
      searchQueryStr,
    });
    const query = searchParams.queryString;
    if (!query) return;

    this.previousPathSearch = searchQueryStr;
    this.query = query;
    void this.search();
  }

  setQuery(query: string) {
    this.query = query;
  }

  submitSearch() {
    const query = this.query.trim();
    if (!query) return;

    void this.search();
  }

  get rows(): AgentSearchResultRow[] {
    const emitted = new Set<string>();
    const rows: AgentSearchResultRow[] = [];
    let lastHeadingLabel = "";
    const shouldRenderFastResultRows = this.groups.length === 0 && !this.hasAgentReordered;

    for (const [index, sectionRow] of this.fastSearchSectionRows.entries()) {
      if (sectionRow.kind === "heading") {
        const title = this.getHeadingLabel(sectionRow.result);
        if (title === lastHeadingLabel) continue;
        if (
          !this.hasRenderableFastSearchSectionRows({
            headingIndex: index,
            shouldRenderFastResultRows,
          })
        ) {
          continue;
        }
        lastHeadingLabel = title;
        rows.push({ kind: "heading", result: sectionRow.result });
        continue;
      }

      if (sectionRow.kind === "carousel") {
        rows.push({ kind: "carousel", result: sectionRow.result });
        continue;
      }

      if (!shouldRenderFastResultRows) continue;

      const result = this.resultById.get(sectionRow.resultId);
      if (!result) continue;

      rows.push({ kind: "result", result });
      emitted.add(sectionRow.resultId);
    }

    let hasGroupedResults = false;

    for (const group of this.groups) {
      const resultRows: AgentSearchResultRow[] = [];
      for (const resultId of group.resultIds) {
        const resolvedResultId = this.resolveResultId(resultId);
        if (!resolvedResultId || emitted.has(resolvedResultId)) continue;

        const result = this.resultById.get(resolvedResultId);
        if (!result) continue;

        resultRows.push({ kind: "result", result });
        emitted.add(resolvedResultId);
      }
      if (resultRows.length === 0) continue;
      rows.push({ kind: "group", group });
      rows.push(...resultRows);
      hasGroupedResults = true;
    }

    let hasOtherResultsHeader = false;
    for (const resultId of this.visibleResultIds) {
      if (emitted.has(resultId)) continue;
      const result = this.resultById.get(resultId);
      if (!result) continue;

      if (hasGroupedResults && !hasOtherResultsHeader) {
        rows.push({ kind: "otherResultsHeader" });
        hasOtherResultsHeader = true;
      }
      rows.push({ kind: "result", result });
    }

    return rows;
  }

  get orderedItemIds(): string[] {
    return this.rows
      .map(row => (row.kind === "result" ? row.result : null))
      .map(result =>
        result ? (this.getResultNoteId(result) ?? this.getResultCollectionId(result)) : null
      )
      .filter((resultId): resultId is string => Boolean(resultId));
  }

  async search() {
    const query = this.query.trim();
    ++this.searchRequestId;
    this.unsubscribe();
    this.resetSearchState();

    if (!query) return;

    void this.buildSearchRequest();
    this.errorMessage = "Agentic search is no longer available.";
    this.isDone = true;
  }

  async loadNextFastPage() {
    this.nextFastSearchCursor = null;
    this.isLoadingNextPage = false;
  }

  handlePatch(data: AgenticSearchPatchEventData) {
    if (!this.agenticSearchSessionId) return;
    if (data.agentic_search_session_id !== this.agenticSearchSessionId) return;
    if (data.sequence <= this.latestSequence) return;

    this.latestSequence = data.sequence;
    switch (data.kind) {
      case "status":
        this.statusMessage = data.payload.message;
        break;
      case "update_results":
        this.applyResultUpdates(data.payload);
        break;
      case "group_results":
        this.applyGroups(data.payload);
        break;
      case "reorder_results":
        this.applyReorder(data.payload);
        break;
      case "done":
        this.forceAgentRunDone();
        break;
      case "failed":
        this.clearAgentRunTimeout();
        this.isAgentRunning = false;
        this.errorMessage = data.payload.message;
        break;
    }
  }

  private applyResultUpdates(payload: {
    status_message?: string | null;
    results?: unknown[];
    updates?: AgenticSearchResultUpdate[];
  }) {
    if (payload.status_message) this.statusMessage = payload.status_message;

    this.addResults(payload.results ?? [], true);

    for (const update of payload.updates ?? []) {
      const target: AgenticSearchResultTarget = {
        result_id: update.result_id,
        note_id: update.note_id,
        collection_id: update.collection_id,
      };
      const resultId =
        this.resolveTarget(target) ??
        (update.append ? this.registerUpdateTargetResult(target) : null);
      if (!resultId) continue;

      if (update.append) {
        this.appendedResultIds.add(resultId);
        this.highlightedResultIds.add(resultId);
      }
      if (update.hint) this.resultHints.set(resultId, update.hint);
    }
  }

  getResultId(result: SearchEngineResult): string | null {
    const value = this.getResultValue(result);
    const metadata = value?.metadata as Record<string, unknown> | undefined;
    return (
      stringValue(metadata?.search_engine_result_id) ??
      stringValue(value?.note_id) ??
      stringValue(value?.collection_id)
    );
  }

  getResultTitle(result: SearchEngineResult): string {
    const item = this.getResultItem(result);
    const value = this.getResultValue(result);

    if (item?.kind === "NOTE") {
      const noteId = this.getResultNoteId(result);
      const note = noteId ? this.store.notes.get(noteId as Uuid) : undefined;
      return note?.title ?? stringValue(value?.title) ?? "Untitled note";
    }

    if (item?.kind === "COLLECTION") {
      const collectionId = this.getResultCollectionId(result);
      const collection = collectionId
        ? this.store.collections.getCollectionObservableById({ collectionId: collectionId as Uuid })
        : undefined;
      return (
        collection?.label ??
        stringValue(value?.label) ??
        stringValue(value?.title) ??
        "Untitled collection"
      );
    }

    return "Untitled";
  }

  getResultPreview(result: SearchEngineResult): string {
    const item = this.getResultItem(result);
    const value = this.getResultValue(result);

    if (item?.kind === "NOTE") {
      const noteId = this.getResultNoteId(result);
      const note = noteId ? this.store.notes.get(noteId as Uuid) : undefined;
      return (
        note?.secondaryTitle ??
        stringValue(value?.content_preview) ??
        stringValue(value?.preview) ??
        ""
      );
    }

    return stringValue(value?.content_preview) ?? stringValue(value?.preview) ?? "";
  }

  getResultSnippetRows(
    result: SearchEngineResult,
    secondaryLabel: string
  ): SearchResultFeaturedSnippetRows {
    const item = this.getResultItem(result);
    const value = this.getResultValue(result);

    if (item?.kind !== "NOTE" || !value) return [];

    return resolveSearchResultFeaturedSnippet({
      secondaryLabel,
      itemValue: value as SearchEngineResultItemNoteValue,
    }).filter(snippetRow => snippetRow.some(chunk => chunk.text));
  }

  getResultNoteId(result: SearchEngineResult): string | null {
    const item = this.getResultItem(result);
    if (item?.kind !== "NOTE") return null;

    return stringValue(this.getResultValue(result)?.note_id);
  }

  getResultCollectionId(result: SearchEngineResult): string | null {
    const item = this.getResultItem(result);
    if (item?.kind !== "COLLECTION") return null;

    return stringValue(this.getResultValue(result)?.collection_id);
  }

  getResultHint(resultId: string): string | null {
    return this.resultHints.get(resultId) ?? null;
  }

  getAttachmentPreview(
    result: SearchEngineResult,
    noteObservable: INoteObservable
  ): AgentSearchAttachmentPreview | null {
    const value = this.getResultValue(result);
    if (!value || this.getResultItem(result)?.kind !== "NOTE") return null;

    const noteValue = value as SearchEngineResultItemNoteValue;
    const attachmentMatches = this.resolveAttachmentMatches({ noteValue });
    if (!attachmentMatches.length) return null;

    if (!noteObservable.isNoteContentDocumentLoaded) {
      this.prefetchAttachmentPreviewContent({ result });
      return null;
    }

    const pdfMatches = this.resolvePdfAttachmentMatches({ noteValue });
    const imageMatches = this.resolveImageAttachmentMatches({ noteValue });
    const pdfChips = this.resolvePdfChipItems({
      attachmentMatches: pdfMatches,
      noteObservable,
    });
    const imageThumbnails = this.resolveImageThumbnailItems({
      attachmentMatches: imageMatches,
      noteObservable,
    });

    if (!pdfChips.length && !imageThumbnails.length) return null;

    return {
      pdfChips,
      imageThumbnails,
      mediaOrder: resolveSearchResultAttachmentMediaOrder({
        attachmentMatches,
        pdfMatches,
        imageMatches,
      }),
    };
  }

  closePdfLightbox() {
    this.pdfLightboxState = null;
  }

  closeImageLightbox() {
    this.imageLightboxState = null;
  }

  dispose() {
    this.clearStatusMessageDismissTimeout();
    this.clearAgentRunTimeout();
    this.unsubscribe();
    this.dateFilterDisposer();
  }

  private clearStatusMessageDismissTimeout() {
    if (this.statusMessageDismissTimeoutId === null) return;

    clearTimeout(this.statusMessageDismissTimeoutId);
    this.statusMessageDismissTimeoutId = null;
  }

  private scheduleStatusMessageDismiss() {
    this.clearStatusMessageDismissTimeout();

    if (!this.statusMessage) return;

    this.statusMessageDismissTimeoutId = setTimeout(() => {
      runInAction(() => {
        this.statusMessage = "";
        this.statusMessageDismissTimeoutId = null;
      });
    }, AGENT_SEARCH_STATUS_MESSAGE_DISMISS_DELAY_MS);
  }

  private clearAgentRunTimeout() {
    if (this.agentRunTimeoutId === null) return;

    clearTimeout(this.agentRunTimeoutId);
    this.agentRunTimeoutId = null;
  }

  private scheduleAgentRunTimeout() {
    this.clearAgentRunTimeout();

    this.agentRunTimeoutId = setTimeout(() => {
      runInAction(() => {
        this.agentRunTimeoutId = null;
        this.forceAgentRunDone();
      });
    }, AGENT_SEARCH_RUN_TIMEOUT_MS);
  }

  private forceAgentRunDone() {
    if (!this.isAgentRunning) return;

    this.clearAgentRunTimeout();
    this.isAgentRunning = false;
    this.isDone = true;
    this.scheduleStatusMessageDismiss();
  }

  private resetSearchState() {
    this.clearStatusMessageDismissTimeout();
    this.clearAgentRunTimeout();
    this.agenticSearchSessionId = null;
    this.pusherChannelName = null;
    this.runId = null;
    this.runMode = null;
    this.latestSequence = 0;
    this.statusMessage = "";
    this.isAgentRunning = false;
    this.isLoadingNextPage = false;
    this.isDone = false;
    this.errorMessage = null;
    this.resultById.clear();
    this.visibleResultIds = [];
    this.appendedResultIds.clear();
    this.highlightedResultIds.clear();
    this.resultHints.clear();
    this.groups = [];
    this.fastSearchSectionRows = [];
    this.hasAgentReordered = false;
    this.nextFastSearchCursor = null;
  }

  private buildSearchRequest(): SearchEngineRequest {
    const searchParams = this.filters.params;
    const sortBy = searchParams.sortBy ?? SortByKind.Relevance;

    return {
      space_id: this.store.spaces.activeSpaceId as Uuid,
      pagination_info: {
        sort_by: sortBy,
        page_size: 40,
      },
      query: this.query.trim(),
      filters: [
        ...searchParams.filters,
        ...convertDateParamsToSearchEngineFilters(this.filters.dateParams, sortBy),
      ],
      facet_filters: searchParams.facetFilters,
      config: {
        supported_section_kinds: ["HEADING", "ITEM", "CAROUSEL"],
      },
    };
  }

  private subscribe(channelName: string) {
    this.pusherChannel = this.pusher.subscribe(channelName);
    this.pusherChannel.bind(PusherEventKind.AGENTIC_SEARCH_PATCH, this.handlePatch);
  }

  private unsubscribe() {
    if (!this.pusherChannel) return;
    this.pusherChannel.unbind(PusherEventKind.AGENTIC_SEARCH_PATCH, this.handlePatch);
    this.pusher.unsubscribe(this.pusherChannel.name);
    this.pusherChannel = null;
  }

  getHeadingLabel(result: SearchEngineResult): string {
    if (result.kind !== "HEADING") return "";
    return (result.value as ApiSchema["SearchEngineResultSectionHeadingValue"]).label ?? "";
  }

  private addFastSearchResults(results: unknown[]) {
    for (const result of results) {
      const normalizedResult = this.normalizeResult(result);
      if (!normalizedResult) continue;

      if (normalizedResult.kind === "HEADING") {
        this.fastSearchSectionRows.push({ kind: "heading", result: normalizedResult });
        continue;
      }

      if (normalizedResult.kind === "CAROUSEL") {
        this.fastSearchSectionRows.push({ kind: "carousel", result: normalizedResult });
        continue;
      }

      const resultId = this.registerItemResult(normalizedResult);
      if (!resultId) continue;

      this.fastSearchSectionRows.push({ kind: "result", resultId });
    }
  }

  private hasRenderableFastSearchSectionRows({
    headingIndex,
    shouldRenderFastResultRows,
  }: {
    headingIndex: number;
    shouldRenderFastResultRows: boolean;
  }): boolean {
    for (const sectionRow of this.fastSearchSectionRows.slice(headingIndex + 1)) {
      if (sectionRow.kind === "heading") return false;
      if (sectionRow.kind === "carousel") return true;
      if (shouldRenderFastResultRows && this.resultById.has(sectionRow.resultId)) return true;
    }

    return false;
  }

  private registerItemResult(
    normalizedResult: SearchEngineResult,
    options?: { appended?: boolean }
  ): string | null {
    const resultId = this.getResultId(normalizedResult);
    if (!resultId) return null;

    const existingResultId = this.resolveExistingEntityResultId(normalizedResult);
    const visibleResultId = existingResultId ?? resultId;
    const visibleResult = existingResultId
      ? this.resultById.get(existingResultId)
      : normalizedResult;
    if (!existingResultId) {
      this.resultById.set(visibleResultId, normalizedResult);
    }
    if (!this.visibleResultIds.includes(visibleResultId)) {
      this.visibleResultIds.push(visibleResultId);
    }
    if (options?.appended && !existingResultId) {
      this.appendedResultIds.add(visibleResultId);
      this.highlightedResultIds.add(visibleResultId);
    }
    this.prefetchAttachmentPreviewContent({ result: visibleResult ?? normalizedResult });

    return visibleResultId;
  }

  private registerUpdateTargetResult(target: AgenticSearchResultTarget): string | null {
    const normalizedResult = this.normalizeResult(target);
    if (!normalizedResult) return null;

    return this.registerItemResult(normalizedResult);
  }

  private addResults(results: unknown[], appended = false): string[] {
    const addedResultIds: string[] = [];

    for (const result of results) {
      const normalizedResult = this.normalizeResult(result);
      if (!normalizedResult) continue;

      const resultId = this.registerItemResult(normalizedResult, { appended });
      if (!resultId) continue;

      addedResultIds.push(resultId);
    }

    return addedResultIds;
  }

  private prefetchAttachmentPreviewContent({ result }: { result: SearchEngineResult }) {
    const noteId = this.getResultNoteId(result);
    if (!noteId) return;

    const noteValue = this.getResultValue(result) as SearchEngineResultItemNoteValue | undefined;
    if (!noteValue || !this.resolveAttachmentMatches({ noteValue }).length) return;

    const noteObservable = this.store.notes.get(noteId as Uuid);
    if (!noteObservable || noteObservable.isNoteContentDocumentLoaded) return;
    if (this.prefetchedAttachmentContentNoteIds.has(noteObservable.id)) return;

    this.prefetchedAttachmentContentNoteIds.add(noteObservable.id);
    void noteObservable.fetchNoteContentDocument().finally(() => {
      this.prefetchedAttachmentContentNoteIds.delete(noteObservable.id);
    });
  }

  private applyGroups(payload: { groups: AgentSearchResultGroup[] }) {
    const groups = Array.isArray(payload.groups) ? payload.groups : [];
    this.groups = groups
      .map(group => normalizeGroup(group))
      .filter((group): group is AgentSearchResultGroup => Boolean(group));
  }

  private applyReorder(payload: { resultIds: string[] }) {
    const resultIds = Array.isArray(payload.resultIds) ? payload.resultIds : [];
    const knownOrderedIds = resultIds
      .map(resultId => this.resolveResultId(resultId))
      .filter((resultId): resultId is string => Boolean(resultId));
    if (knownOrderedIds.length === 0) return;

    this.hasAgentReordered = true;
    const orderByResultId = new Map(knownOrderedIds.map((resultId, index) => [resultId, index]));
    const remainingIds = this.visibleResultIds.filter(
      resultId => !knownOrderedIds.includes(resultId)
    );
    this.visibleResultIds = [...knownOrderedIds, ...remainingIds];
    this.groups = this.groups
      .map(group => ({
        ...group,
        resultIds: [...group.resultIds].sort((left, right) => {
          const leftId = this.resolveResultId(left);
          const rightId = this.resolveResultId(right);
          const leftOrder = leftId ? orderByResultId.get(leftId) : undefined;
          const rightOrder = rightId ? orderByResultId.get(rightId) : undefined;

          if (leftOrder === undefined && rightOrder === undefined) return 0;
          if (leftOrder === undefined) return 1;
          if (rightOrder === undefined) return -1;
          return leftOrder - rightOrder;
        }),
      }))
      .sort((left, right) => {
        const leftOrder = resolveGroupFirstOrder(left, orderByResultId, candidate =>
          this.resolveResultId(candidate)
        );
        const rightOrder = resolveGroupFirstOrder(right, orderByResultId, candidate =>
          this.resolveResultId(candidate)
        );

        if (leftOrder === undefined && rightOrder === undefined) return 0;
        if (leftOrder === undefined) return 1;
        if (rightOrder === undefined) return -1;
        return leftOrder - rightOrder;
      });
  }

  private resolveTarget(target: unknown): string | null {
    if (!target || typeof target !== "object") return null;
    const record = target as Record<string, unknown>;
    const candidates = [
      stringValue(record.result_id),
      stringValue(record.resultId),
      stringValue(record.note_id),
      stringValue(record.noteId),
      stringValue(record.collection_id),
      stringValue(record.collectionId),
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;

      const resultId = this.resolveResultId(candidate);
      if (resultId) return resultId;
    }

    return null;
  }

  private resolveResultId(candidate: string): string | null {
    if (this.resultById.has(candidate)) return candidate;

    for (const [resultId, result] of this.resultById) {
      const value = this.getResultValue(result);
      if (candidate === stringValue(value?.note_id)) return resultId;
      if (candidate === stringValue(value?.collection_id)) return resultId;
    }

    return null;
  }

  private resolveExistingEntityResultId(result: SearchEngineResult): string | null {
    const value = this.getResultValue(result);
    const noteId = stringValue(value?.note_id);
    const collectionId = stringValue(value?.collection_id);

    if (!noteId && !collectionId) return null;

    for (const [resultId, existingResult] of this.resultById) {
      const existingValue = this.getResultValue(existingResult);
      if (noteId && noteId === stringValue(existingValue?.note_id)) return resultId;
      if (collectionId && collectionId === stringValue(existingValue?.collection_id)) {
        return resultId;
      }
    }

    return null;
  }

  private normalizeResult(value: unknown): SearchEngineResult | null {
    if (!value || typeof value !== "object") return null;

    const record = value as Record<string, unknown>;
    if (record.kind === "ITEM" || record.kind === "HEADING" || record.kind === "CAROUSEL") {
      return value as SearchEngineResult;
    }

    const noteId = stringValue(record.note_id) ?? stringValue(record.noteId);
    if (noteId) {
      return {
        kind: "ITEM",
        value: {
          kind: "NOTE",
          score: { value: 0 },
          value: {
            note_id: noteId,
            title: stringValue(record.title),
            content_preview: stringValue(record.snippet) ?? stringValue(record.note_summary),
            attachment_match_infos: resolveAttachmentMatchInfos(record),
            metadata: {
              search_engine_result_id:
                stringValue(record.result_id) ?? stringValue(record.resultId) ?? noteId,
            },
          },
        },
      } as unknown as SearchEngineResult;
    }

    const collectionId = stringValue(record.collection_id) ?? stringValue(record.collectionId);
    if (collectionId) {
      return {
        kind: "ITEM",
        value: {
          kind: "COLLECTION",
          score: { value: 0 },
          value: {
            collection_id: collectionId,
            label: stringValue(record.label) ?? stringValue(record.title),
            metadata: {
              search_engine_result_id:
                stringValue(record.result_id) ?? stringValue(record.resultId) ?? collectionId,
            },
          },
        },
      } as unknown as SearchEngineResult;
    }

    return null;
  }

  private getResultItem(
    result: SearchEngineResult
  ): ApiSchema["SearchEngineResultSectionItemValue"] | undefined {
    if (result.kind !== "ITEM") return undefined;
    return result.value as ApiSchema["SearchEngineResultSectionItemValue"];
  }

  private getResultValue(result: SearchEngineResult): Record<string, unknown> | undefined {
    return this.getResultItem(result)?.value as Record<string, unknown> | undefined;
  }

  private async resolveProxiedAttachmentUrl({ attachmentUrl }: { attachmentUrl: string }) {
    await this.store.auth.assetAccessTokenState.ensureToken();

    const classification = classifyProxyAttachmentUrl({ attachmentUrl });
    if (classification.kind === "already-proxied") {
      return classification.url;
    }

    const proxiedUrl = buildProxiedAssetUrl({
      originalUrl: classification.originalUrl,
      assetAccessTokenState: this.store.auth.assetAccessTokenState,
    });

    return proxiedUrl ?? classification.originalUrl;
  }

  private async openPdfLightbox({
    attachmentUrl,
    fileName,
    initialPage,
  }: {
    attachmentUrl: string;
    fileName: string;
    initialPage?: number;
  }) {
    const proxiedUrl = await this.resolveProxiedAttachmentUrl({ attachmentUrl });

    runInAction(() => {
      this.pdfLightboxState = {
        src: proxiedUrl ?? attachmentUrl,
        fileName,
        initialPage,
      };
    });
  }

  private async openImageLightbox({
    images,
    initialIndex,
  }: {
    images: AgentSearchImageLightboxItem[];
    initialIndex: number;
  }) {
    if (!images.length || initialIndex < 0 || initialIndex >= images.length) {
      return;
    }

    const proxiedImages = await Promise.all(
      images.map(async image => ({
        src: await this.resolveProxiedAttachmentUrl({ attachmentUrl: image.src }),
        alt: image.alt,
      }))
    );

    runInAction(() => {
      this.imageLightboxState = {
        images: proxiedImages,
        initialIndex,
      };
    });
  }

  private resolveAttachmentMatches({
    noteValue,
    attachmentType,
  }: {
    noteValue: SearchEngineResultItemNoteValue;
    attachmentType?: SearchEngineResultAttachmentType;
  }): SearchEngineResultAttachmentMatchInfo[] {
    const attachmentMatchInfos = noteValue.attachment_match_infos;

    if (!attachmentMatchInfos?.length) {
      return [];
    }

    const matches: SearchEngineResultAttachmentMatchInfo[] = [];
    const seenAttachmentIds = new Set<string>();

    for (const attachmentMatchInfo of attachmentMatchInfos) {
      if (attachmentType && attachmentMatchInfo.attachment_type !== attachmentType) {
        continue;
      }

      if (
        !attachmentMatchInfo.attachment_id ||
        seenAttachmentIds.has(attachmentMatchInfo.attachment_id)
      ) {
        continue;
      }

      seenAttachmentIds.add(attachmentMatchInfo.attachment_id);
      matches.push(attachmentMatchInfo);
    }

    return matches;
  }

  private resolvePdfAttachmentMatches({
    noteValue,
  }: {
    noteValue: SearchEngineResultItemNoteValue;
  }): SearchEngineResultAttachmentMatchInfo[] {
    return this.resolveAttachmentMatches({
      noteValue,
      attachmentType: "pdf",
    });
  }

  private resolveImageAttachmentMatches({
    noteValue,
  }: {
    noteValue: SearchEngineResultItemNoteValue;
  }): SearchEngineResultAttachmentMatchInfo[] {
    return this.resolveAttachmentMatches({
      noteValue,
      attachmentType: "image",
    });
  }

  private resolvePdfChipItems({
    attachmentMatches,
    noteObservable,
  }: {
    attachmentMatches: SearchEngineResultAttachmentMatchInfo[];
    noteObservable: INoteObservable;
  }): SearchResultPdfChipItem[] {
    if (!attachmentMatches.length) {
      return [];
    }

    const fileAttachments = this.resolveFileAttachmentsFromLoadedNoteContent({
      noteObservable,
    });

    return attachmentMatches.map(attachmentMatchInfo => {
      const matchingFileAttachment = this.resolveMatchingFileAttachment({
        searchAttachmentUrl: attachmentMatchInfo.attachment_url,
        fileAttachments,
      });

      const fileName = matchingFileAttachment?.filename || attachmentMatchInfo.filename || "PDF";

      return {
        id: `${attachmentMatchInfo.attachment_id}-${attachmentMatchInfo.page_number ?? 0}`,
        label: fileName,
        fileSizeBytes: matchingFileAttachment?.sizeBytes ?? null,
        showSyncedStatus: false,
        onClick: () => {
          void this.openPdfAttachmentLightbox({
            noteObservable,
            attachmentMatchInfo,
            fallbackFileName: fileName,
          });
        },
      };
    });
  }

  private resolveImageThumbnailItems({
    attachmentMatches,
    noteObservable,
  }: {
    attachmentMatches: SearchEngineResultAttachmentMatchInfo[];
    noteObservable: INoteObservable;
  }): SearchResultImageThumbnailItem[] {
    if (!attachmentMatches.length) {
      return [];
    }

    void this.store.auth.assetAccessTokenState.ensureToken();

    const imageAttachments = this.resolveImageAttachmentsFromLoadedNoteContent({
      noteObservable,
    });

    const lightboxItems: AgentSearchImageLightboxItem[] = attachmentMatches
      .map(attachmentMatchInfo => {
        const matchingImageAttachment = this.resolveMatchingImageAttachment({
          searchAttachmentUrl: attachmentMatchInfo.attachment_url,
          imageAttachments,
        });
        const imageUrl = matchingImageAttachment?.url;
        if (!imageUrl) {
          return null;
        }

        return {
          id: attachmentMatchInfo.attachment_id,
          src: imageUrl,
          alt:
            matchingImageAttachment?.alt ||
            matchingImageAttachment?.name ||
            matchingImageAttachment?.filename ||
            attachmentMatchInfo.filename ||
            "Image attachment",
        };
      })
      .filter((item): item is AgentSearchImageLightboxItem => !!item);

    return lightboxItems.map((lightboxItem, index) => ({
      id: lightboxItem.id,
      src:
        buildProxiedAssetUrl({
          originalUrl: lightboxItem.src,
          assetAccessTokenState: this.store.auth.assetAccessTokenState,
        }) ?? lightboxItem.src,
      alt: lightboxItem.alt,
      onClick: () => {
        void this.openImageLightbox({ images: lightboxItems, initialIndex: index });
      },
    }));
  }

  private async openPdfAttachmentLightbox({
    noteObservable,
    attachmentMatchInfo,
    fallbackFileName,
  }: {
    noteObservable: INoteObservable;
    attachmentMatchInfo: SearchEngineResultAttachmentMatchInfo;
    fallbackFileName: string;
  }) {
    const fileAttachments = this.resolveFileAttachmentsFromLoadedNoteContent({
      noteObservable,
    });
    const matchingFileAttachment = this.resolveMatchingFileAttachment({
      searchAttachmentUrl: attachmentMatchInfo.attachment_url,
      fileAttachments,
    });

    const attachmentUrl =
      matchingFileAttachment?.url ||
      (await this.resolveAttachmentUrlFromNoteContent({
        noteObservable,
        searchAttachmentUrl: attachmentMatchInfo.attachment_url,
        attachmentType: "file",
      }));

    if (!attachmentUrl) {
      return;
    }

    const fileName =
      matchingFileAttachment?.filename || attachmentMatchInfo.filename || fallbackFileName;

    await this.openPdfLightbox({
      attachmentUrl,
      fileName,
      initialPage:
        typeof attachmentMatchInfo.page_number === "number"
          ? attachmentMatchInfo.page_number
          : undefined,
    });
  }

  private async resolveAttachmentUrlFromNoteContent({
    noteObservable,
    searchAttachmentUrl,
    attachmentType,
  }: {
    noteObservable: INoteObservable;
    searchAttachmentUrl?: string | null;
    attachmentType: SearchResultMediaAttachment["type"];
  }): Promise<string | null> {
    await noteObservable.fetchNoteContentDocument();

    if (attachmentType === "file") {
      const fileAttachments = this.resolveFileAttachmentsFromLoadedNoteContent({
        noteObservable,
      });
      const matchingFileAttachment = this.resolveMatchingFileAttachment({
        searchAttachmentUrl,
        fileAttachments,
      });

      return matchingFileAttachment?.url ?? null;
    }

    const imageAttachments = this.resolveImageAttachmentsFromLoadedNoteContent({
      noteObservable,
    });
    const matchingImageAttachment = this.resolveMatchingImageAttachment({
      searchAttachmentUrl,
      imageAttachments,
    });

    return matchingImageAttachment?.url ?? null;
  }

  private resolveAttachmentsFromLoadedNoteContent({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): SearchResultMediaAttachment[] {
    const encodedContent = noteObservable.remoteContent;

    if (!encodedContent) {
      return [];
    }

    const extractedContent = noteModule.extractContentFromEncodedContent({
      encodedContent,
    });

    return extractedContent.attachments.filter(
      (attachment): attachment is SearchResultMediaAttachment =>
        attachment.type === "file" || attachment.type === "image"
    );
  }

  private resolveFileAttachmentsFromLoadedNoteContent({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): SearchResultFileAttachment[] {
    return this.resolveAttachmentsFromLoadedNoteContent({ noteObservable }).filter(
      (attachment): attachment is SearchResultFileAttachment => attachment.type === "file"
    );
  }

  private resolveImageAttachmentsFromLoadedNoteContent({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): SearchResultImageAttachment[] {
    return this.resolveAttachmentsFromLoadedNoteContent({ noteObservable }).filter(
      (attachment): attachment is SearchResultImageAttachment => attachment.type === "image"
    );
  }

  private resolveMatchingFileAttachment({
    searchAttachmentUrl,
    fileAttachments,
  }: {
    searchAttachmentUrl?: string | null;
    fileAttachments: SearchResultFileAttachment[];
  }): SearchResultFileAttachment | null {
    return resolveMatchingAttachmentByUrl({
      searchAttachmentUrl,
      attachments: fileAttachments,
    });
  }

  private resolveMatchingImageAttachment({
    searchAttachmentUrl,
    imageAttachments,
  }: {
    searchAttachmentUrl?: string | null;
    imageAttachments: SearchResultImageAttachment[];
  }): SearchResultImageAttachment | null {
    return resolveMatchingAttachmentByUrl({
      searchAttachmentUrl,
      attachments: imageAttachments,
    });
  }

  private resolvePathSearch(): string {
    const stripLeadingQuestionMark = (search: string) =>
      search.startsWith("?") ? search.slice(1) : search;

    if (typeof this.path === "string") {
      return stripLeadingQuestionMark(this.path.split("?")[1] ?? "");
    }
    return stripLeadingQuestionMark(this.path.search ?? "");
  }
}

const stringValue = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  return value.length ? value : null;
};

const resolveAttachmentMatchInfos = (
  record: Record<string, unknown>
): SearchEngineResultItemNoteValue["attachment_match_infos"] | undefined => {
  const value = record.attachment_match_infos ?? record.attachment_matches;
  if (!Array.isArray(value) || value.length === 0) return undefined;

  return value.map(match => {
    if (!match || typeof match !== "object") return match;
    const attachmentMatch = match as Record<string, unknown>;
    return {
      ...attachmentMatch,
      attachment_type: attachmentMatch.attachment_type ?? attachmentMatch.attachment_kind,
    };
  }) as SearchEngineResultItemNoteValue["attachment_match_infos"];
};

const normalizeGroup = (value: unknown): AgentSearchResultGroup | null => {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = stringValue(record.id);
  const label = stringValue(record.label);
  let rawResultIds: unknown[] = [];
  if (Array.isArray(record.resultIds)) {
    rawResultIds = record.resultIds;
  } else if (Array.isArray(record.result_ids)) {
    rawResultIds = record.result_ids;
  }
  const resultIds = rawResultIds.map(String);
  if (!id || !label) return null;
  return { id, label, resultIds };
};

const resolveGroupFirstOrder = (
  group: AgentSearchResultGroup,
  orderByResultId: Map<string, number>,
  resolveResultId: (candidate: string) => string | null
): number | undefined => {
  for (const resultId of group.resultIds) {
    const resolvedResultId = resolveResultId(resultId);
    if (!resolvedResultId) continue;

    const order = orderByResultId.get(resolvedResultId);
    if (order !== undefined) return order;
  }

  return undefined;
};

type SearchEngineResultItemNoteValue = ApiSchema["SearchEngineResultSectionItemNoteValue"];
type SearchEngineResultAttachmentMatchInfo = NonNullable<
  SearchEngineResultItemNoteValue["attachment_match_infos"]
>[number];
type SearchEngineResultAttachmentType = SearchEngineResultAttachmentMatchInfo["attachment_type"];

interface AgentSearchPdfLightboxState {
  src: string;
  fileName: string;
  initialPage?: number;
}

interface AgentSearchImageLightboxState {
  images: IImageLightboxImage[];
  initialIndex: number;
}

type AgentSearchImageLightboxItem = {
  id: string;
  src: string;
  alt: string;
};

type SearchResultFileAttachment = {
  type: "file";
  id: string | null;
  filename?: string;
  sizeBytes?: number;
  url?: string;
};

type SearchResultImageAttachment = {
  type: "image";
  id: string | null;
  filename?: string;
  name?: string;
  alt?: string;
  url?: string;
};

type SearchResultMediaAttachment = SearchResultFileAttachment | SearchResultImageAttachment;
